import { cn } from '@/utils/styles/cn'
import { memo, useMemo } from 'react'

type ILoadingSpinnerProps = {
    size: number
    colorClassName?: string
    strokeWidth?: number
    className?: string
    colorStyle?: string
    initialDelay?: number
}

export const LoadingSpinner = memo(function LoadingSpinner({
    size,
    colorClassName,
    colorStyle,
    className,
    strokeWidth = 7,
    initialDelay = 0,
}: ILoadingSpinnerProps) {
    // Fade-in animation for the container (doesn't use transform)
    const containerStyle = useMemo(
        () =>
            initialDelay > 0
                ? {
                      animation: `spinner-fade-in 0.2s ease-out ${initialDelay}s both`,
                  }
                : undefined,
        [initialDelay]
    )

    // Rotation animation on the SVG only (separate from container transforms)
    // Using will-change for GPU acceleration
    const svgStyle = useMemo(
        () => ({
            animation: 'spinner-rotate 0.55s linear infinite',
            willChange: 'transform',
        }),
        []
    )

    return (
        <div className={cn('flex items-center justify-center', className)} style={containerStyle}>
            <svg
                strokeDasharray={150}
                strokeWidth={strokeWidth}
                strokeDashoffset={0}
                viewBox="0 0 100 100"
                width={size}
                height={size}
                style={svgStyle}
            >
                <circle
                    cx="50"
                    cy="50"
                    r="32"
                    fill="transparent"
                    style={colorStyle ? { stroke: colorStyle } : undefined}
                    className={cn('stroke-foreground', colorClassName)}
                />
            </svg>
        </div>
    )
})

/**
 * LoadingSpinner2 - Futuristic spacecraft-style loading spinner
 *
 * Features a boot-up animation that simulates an engine spooling up:
 * - Starts slow with a subtle glow
 * - Exponentially accelerates to full speed
 * - Maintains smooth, fast rotation once "online"
 *
 * The animation creates the feeling of a spacecraft system powering up,
 * with the spinner gaining momentum before reaching operational speed.
 */
type ILoadingSpinner2Props = {
    size: number
    colorClassName?: string
    strokeWidth?: number
    className?: string
    colorStyle?: string
    initialDelay?: number
    /** Duration of the boot-up acceleration phase in seconds (default: 0.5) */
    bootDuration?: number
    /** Whether to show the subtle outer ring (default: true) */
    showOuterRing?: boolean
}

export const LoadingSpinner2 = memo(function LoadingSpinner2({
    size,
    colorClassName,
    colorStyle,
    className,
    strokeWidth = 7,
    initialDelay = 0,
    bootDuration = 0.5,
    showOuterRing = true,
}: ILoadingSpinner2Props) {
    // Arc geometry - longer arc for more presence
    const radius = 36
    const circumference = 2 * Math.PI * radius
    const arcLength = circumference * 0.38 // ~135 degree arc
    const gapLength = circumference - arcLength

    // Outer ring geometry (larger, fainter)
    const outerRadius = 42
    const outerCircumference = 2.5 * Math.PI * outerRadius
    const outerArcLength = outerCircumference * 0.15 // Smaller arc
    const outerGapLength = outerCircumference - outerArcLength

    // Container glow animation - snappy fade in
    const containerStyle = useMemo(
        () => ({
            animation:
                initialDelay > 0
                    ? `spinner2-glow-pulse ${bootDuration}s cubic-bezier(0.22, 0.61, 0.36, 1) ${initialDelay}s both`
                    : `spinner2-glow-pulse ${bootDuration}s cubic-bezier(0.22, 0.61, 0.36, 1) both`,
        }),
        [initialDelay, bootDuration]
    )

    // Wrapper rotation: constant speed from the start (terminal velocity)
    // This runs forever at the target speed
    const wrapperStyle = useMemo(
        () => ({
            animation: `spinner2-rotate 0.5s linear ${initialDelay}s infinite`,
            willChange: 'transform',
        }),
        [initialDelay]
    )

    // Inner bootup: adds extra rotation that creates acceleration feel
    // Uses ease-out so it starts fast (adding to wrapper = high speed) and decelerates to 0 (wrapper speed only)
    // The result: smooth acceleration that seamlessly reaches terminal velocity
    const spinnerStyle = useMemo(
        () => ({
            animation: `spinner2-bootup ${bootDuration}s cubic-bezier(0.16, 1, 0.3, 1) ${initialDelay}s forwards`,
            willChange: 'transform',
        }),
        [bootDuration, initialDelay]
    )

    // Outer ring: counter-rotation kicks in early for futuristic layered effect
    const outerRingStyle = useMemo(
        () => ({
            animation: `spinner2-outer-ring 1.1s linear ${initialDelay}s infinite`,
            willChange: 'transform',
            opacity: 0.85,
        }),
        [initialDelay]
    )

    return (
        <div className={cn('relative flex items-center justify-center', className)} style={containerStyle}>
            {/* Outer ring - subtle depth element */}
            {showOuterRing && (
                <svg viewBox="0 0 100 100" width={size} height={size} className="absolute" style={outerRingStyle}>
                    <circle
                        cx="50"
                        cy="50"
                        r={outerRadius}
                        fill="none"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeDasharray={`${outerArcLength} ${outerGapLength}`}
                        style={colorStyle ? { stroke: colorStyle } : undefined}
                        className={cn('stroke-foreground', colorClassName)}
                    />
                </svg>
            )}

            {/* Wrapper for constant rotation + inner for bootup acceleration */}
            <div style={wrapperStyle}>
                <svg viewBox="0 0 100 100" width={size} height={size} style={spinnerStyle}>
                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="none"
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={`${arcLength} ${gapLength}`}
                        style={colorStyle ? { stroke: colorStyle } : undefined}
                        className={cn('stroke-foreground', colorClassName)}
                    />
                </svg>
            </div>
        </div>
    )
})
